import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag, User, Heart, LogOut, Search } from "lucide-react";
import { useCart, useWishlist } from "@/contexts/ShopContext";
import { useAuth } from "@/contexts/AuthContext";
import CurrencyToggle from "@/components/CurrencyToggle";

const LINKS = [
  { label: "Home", href: "/", anchor: "#home" },
  { label: "Collections", href: "/#collections" },
  { label: "About", href: "/#story" },
  { label: "Craftsmanship", href: "/#craftsmanship" },
  { label: "Lookbook", href: "/lookbook" },
  { label: "Journal", href: "/journal" },
  { label: "Contact", href: "/#footer" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [accountMenu, setAccountMenu] = useState(false);
  const { count: cartCount, setDrawerOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const { user, isAuthenticated, openAuth, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Smart nav: if target is on the home page, scroll to anchor; otherwise route to home then scroll.
  const handleNavLink = (e, href) => {
    const hashIdx = href.indexOf("#");
    const path = hashIdx === -1 ? href : href.slice(0, hashIdx) || "/";
    const hash = hashIdx === -1 ? "" : href.slice(hashIdx + 1);
    const onHome = location.pathname === "/";

    if (!hash) {
      // No anchor → real route change. Only scroll-to-top if it's the Home ("/") link and we're already home.
      e.preventDefault();
      setOpen(false);
      if (path === "/" && onHome) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate(path);
        setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
      }
      return;
    }

    e.preventDefault();
    setOpen(false);
    if (onHome) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", `#${hash}`);
    } else {
      navigate(`/#${hash}`);
      // Home page useEffect will pick up the hash and scroll.
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close account menu on route change
  useEffect(() => { setAccountMenu(false); }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "glass py-3" : "py-5"}`}
    >
      <div className="px-6 md:px-10 lg:px-14 flex items-center justify-between gap-6">
        {/* Logo */}
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group shrink-0">
          <div className="w-8 h-8 border border-silver/60 flex items-center justify-center group-hover:border-silver transition-colors duration-500">
            <span className="font-serif text-silver text-lg leading-none">A</span>
          </div>
          <div className="leading-none hidden sm:block">
            <div className="font-serif text-[14px] tracking-[0.3em] uppercase text-white">Asymmetry</div>
            <div className="text-[9px] tracking-[0.4em] uppercase text-silver mt-1">Edits · DenimGlow</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-8" aria-label="primary">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={(e) => handleNavLink(e, l.href)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="link-underline text-[11px] tracking-[0.28em] uppercase text-white/80 hover:text-white"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-3 md:gap-5 text-white/80">
          <CurrencyToggle className="hidden md:inline-flex" />

          {/* Wishlist */}
          <Link
            to="/wishlist"
            aria-label="Wishlist"
            data-testid="navbar-wishlist"
            className="relative hover:text-white transition-colors"
          >
            <Heart size={17} strokeWidth={1.2} />
            {wishCount > 0 && (
              <span data-testid="wishlist-count" className="absolute -top-1.5 -right-2 text-[9px] tracking-widest text-silver">
                {wishCount}
              </span>
            )}
          </Link>

          {/* Cart */}
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Bag"
            data-testid="navbar-bag"
            className="relative hover:text-white transition-colors"
          >
            <ShoppingBag size={17} strokeWidth={1.2} />
            {cartCount > 0 && (
              <span data-testid="cart-count" className="absolute -top-1.5 -right-2 text-[9px] tracking-widest text-silver">
                {cartCount}
              </span>
            )}
          </button>

          {/* Account */}
          {isAuthenticated ? (
            <div className="relative">
              <button
                onClick={() => setAccountMenu((v) => !v)}
                aria-label="Account"
                data-testid="navbar-account"
                className="flex items-center gap-2 hover:text-white"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover border border-silver/40" />
                ) : (
                  <User size={17} strokeWidth={1.2} />
                )}
              </button>
              {accountMenu && (
                <div className="absolute right-0 top-10 w-56 bg-midnight-deep border border-white/10 py-3 shadow-xl">
                  <div className="px-4 py-2 border-b border-white/8">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-silver/70">Signed in</div>
                    <div className="text-white text-sm truncate">{user.email || user.phone || user.name}</div>
                  </div>
                  <Link to="/orders" data-testid="account-orders" className="block px-4 py-2 text-[11px] tracking-[0.28em] uppercase text-white/80 hover:text-white hover:bg-white/5">
                    My Orders
                  </Link>
                  <Link to="/wishlist" className="block px-4 py-2 text-[11px] tracking-[0.28em] uppercase text-white/80 hover:text-white hover:bg-white/5">
                    Wishlist
                  </Link>
                  <button
                    onClick={async () => { await logout(); setAccountMenu(false); navigate("/"); }}
                    data-testid="account-logout"
                    className="w-full text-left px-4 py-2 text-[11px] tracking-[0.28em] uppercase text-white/80 hover:text-white hover:bg-white/5 flex items-center gap-2"
                  >
                    <LogOut size={12} /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={openAuth}
              data-testid="navbar-signin"
              className="text-[11px] tracking-[0.28em] uppercase hover:text-white"
            >
              Sign in
            </button>
          )}

          {/* Mobile burger */}
          <button
            data-testid="navbar-mobile-toggle"
            className="xl:hidden text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} strokeWidth={1.2} /> : <Menu size={22} strokeWidth={1.2} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="navbar-mobile-menu" className="xl:hidden glass border-t border-white/10">
          <nav className="flex flex-col px-6 py-8 gap-5">
            {LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={(e) => handleNavLink(e, l.href)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className="text-[12px] tracking-[0.32em] uppercase text-white/85 hover:text-white"
              >{l.label}</a>
            ))}
            <div className="pt-3 border-t border-white/8">
              <CurrencyToggle />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
